import { useMemo, useRef } from 'react';
import type { DatePickerProps } from 'react-datepicker';
import BasicDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

import { MEDIA_QUERY_MIN_WIDTH } from '~shared/lib/responsive';

import { endOfDay, startOfDay, subMonths } from 'date-fns';

import './date-range-picker.css';
import { CustomHeader } from './ui/custom-header/custom-header';
import { Trigger } from './ui/trigger/trigger';

import { Clickable } from '../clickable';

export type DateRange = {
  startDate: string | null;
  endDate: string | null;
};

export type DateRangePreset = { title: React.ReactNode; value: DateRange };

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>
  extends
    Omit<
      DatePickerProps,
      | 'name'
      | 'onChange'
      | 'onBlur'
      | 'value'
      | 'selectsRange'
      | 'selectsMultiple'
      | 'formatMultipleDates'
    >,
    UseControllerReturn<TFieldValues, TName> {
  presets?: DateRangePreset[];
  align?: 'start' | 'end';
}

export function DateRangePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>) {
  const {
    field,
    formState: _formState,
    presets,
    align = 'start',
    ...inputProps
  } = props;
  const { value } = field;
  const isDesktop = useMediaQuery({ query: MEDIA_QUERY_MIN_WIDTH.lg });
  const pickerRef = useRef<InstanceType<typeof BasicDatePicker> | null>(null);

  const range = useMemo(
    () => ({
      startDate: value?.startDate && new Date(value.startDate),
      endDate: value?.endDate && new Date(value.endDate),
    }),
    [value]
  );

  return (
    <div className="relative">
      <BasicDatePicker
        {...field}
        {...inputProps}
        ref={(instance) => {
          pickerRef.current = instance;
          field.ref(instance);
        }}
        selectsRange
        allowSameDay
        selected={range.startDate}
        startDate={range.startDate}
        endDate={range.endDate}
        openToDate={isDesktop ? subMonths(new Date(), 1) : new Date()}
        monthsShown={isDesktop ? 2 : 1}
        withPortal={!isDesktop}
        showPopperArrow={false}
        shouldCloseOnSelect={false}
        wrapperClassName="w-full"
        calendarClassName="date-range-picker-calendar"
        popperClassName={
          align === 'end'
            ? 'date-range-picker date-range-picker--align-end'
            : 'date-range-picker'
        }
        dateFormat="MMMM d"
        customInput={<Trigger size={isDesktop ? 'm' : 's'} />}
        renderCustomHeader={(headerProps) => (
          <CustomHeader {...headerProps} withNavigation={!isDesktop} />
        )}
        useWeekdaysShort
        onChange={([startDate, endDate]: [Date | null, Date | null]) => {
          const periodValue: DateRange = {
            startDate: startDate && startOfDay(startDate).toISOString(),
            endDate: endDate && endOfDay(endDate).toISOString(),
          };

          field.onChange(periodValue);

          if (startDate && endDate) {
            pickerRef.current?.setOpen(false);
          }
        }}
      >
        {presets?.length && (
          <div>
            {presets.map((preset) => (
              <div
                className="inline-block p-4"
                key={`${preset.value.startDate}_${preset.value.endDate}`}
              >
                <Clickable
                  onClick={() => {
                    field.onChange(preset.value);
                    pickerRef.current?.setOpen(false);
                  }}
                >
                  <span className="text-m font-medium text-skyro-blue">
                    {preset.title}
                  </span>
                </Clickable>
              </div>
            ))}
          </div>
        )}
      </BasicDatePicker>
    </div>
  );
}
